/**
 * web/src/screens/Notebook/hooks/dates.ts — the Vault date-idea pool seam of
 * useNotebookState (card vncVrGk1hc9i, round two): the pool the capture bar
 * writes date ideas to, its own fetch cadence, and promoting an idea to the
 * partner. Pure move out of the former hooks.ts — no behaviour change.
 */
import { useCallback, useState } from 'react';
import { api } from '../../../shell/bridge';
import { type DateIdeaRow } from '../types';
import type { NotebookFeed } from './feed';

export function useNotebookDates({ busyId, setBusyId }: {
  busyId: NotebookFeed['busyId'];
  setBusyId: NotebookFeed['setBusyId'];
}) {
  // The Vault → Date idea pool (card ZdYBVP203b8a, PRD §6.1): ideas captured
  // here live in the SAME pipeline Us → Dates plans from, so the Date tile
  // shows where they went instead of swallowing them.
  const [dateIdeas, setDateIdeas] = useState<DateIdeaRow[] | null>(null);
  // PRD §6.1 promises a toast on save — the confirmation is the whole
  // contract for a <10s capture you may not look at again.
  const [dateSaved, setDateSaved] = useState(false);

  /** The date pool is fetched on its own cadence: it belongs to a different
   *  endpoint (and, in prod, a different durable object) than the notebook,
   *  so a slow /api/dates must never take the notebook list down with it. */
  const loadDates = useCallback(async () => {
    try {
      const data = await api<{ ideas: DateIdeaRow[] }>('/api/dates');
      setDateIdeas(Array.isArray(data.ideas) ? data.ideas : []);
    } catch (e) {
      console.error('[Pear] date ideas load failed', e);
      setDateIdeas(prev => prev ?? []);
    }
  }, []);

  const sendDate = async (ideaId: string) => {
    if (busyId) return;
    setBusyId(ideaId);
    try {
      // Promote is the same action Us → Dates calls "Send to partner": the
      // idea leaves POOL and enters the decision loop as SUGGESTED.
      await api(`/api/dates/${ideaId}/promote`, { body: {} });
      await loadDates();
    } catch (e) {
      console.error('[Pear] send date idea failed', e);
    } finally {
      setBusyId(null);
    }
  };
  return { dateIdeas, setDateIdeas, dateSaved, setDateSaved, loadDates, sendDate };
}

export type NotebookDates = ReturnType<typeof useNotebookDates>;
